import { useState, useEffect, useCallback } from 'react';
import { listEmailTemplates, previewEmail, testSendEmail, listEmailLogs } from '../api';

// Assistant Config sibling for transactional email templates. Read + preview
// + test-send + recent-log surface. Editing copy isn't supported yet —
// templates live in squish-backend/lib/email.js and ship with code.

export default function EmailConfig() {
  const [tab, setTab] = useState('templates'); // 'templates' | 'logs'
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected]   = useState(null);
  const [preview, setPreview]     = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testTo, setTestTo]       = useState('');
  const [testing, setTesting]     = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError]         = useState('');
  const [logs, setLogs]           = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logFilter, setLogFilter] = useState({ kind: '', to: '' });

  const loadTemplates = useCallback(async () => {
    setError('');
    try {
      const { templates } = await listEmailTemplates();
      setTemplates(templates || []);
      if (templates?.length && !selected) setSelected(templates[0].kind);
    } catch (e) { setError(e.message); }
  }, [selected]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const loadPreview = useCallback(async (kind) => {
    if (!kind) return;
    setPreviewLoading(true); setError(''); setTestResult(null);
    try {
      const p = await previewEmail(kind);
      setPreview(p);
    } catch (e) { setError(e.message); }
    setPreviewLoading(false);
  }, []);

  useEffect(() => { if (selected) loadPreview(selected); }, [selected, loadPreview]);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true); setError('');
    try {
      const { logs } = await listEmailLogs({
        kind: logFilter.kind || undefined,
        to: logFilter.to || undefined,
        limit: 100,
      });
      setLogs(logs || []);
    } catch (e) { setError(e.message); }
    setLogsLoading(false);
  }, [logFilter]);

  useEffect(() => { if (tab === 'logs') loadLogs(); }, [tab, loadLogs]);

  async function runTestSend() {
    if (!testTo.includes('@')) { setError('Enter a valid email'); return; }
    setTesting(true); setError(''); setTestResult(null);
    try {
      const result = await testSendEmail({ kind: selected, to: testTo });
      setTestResult(result);
    } catch (e) { setError(e.message); }
    setTesting(false);
  }

  return (
    <div style={{ padding: 24, fontFamily: 'var(--font)', color: 'var(--text)', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Email</h1>
        <p style={{ color: 'var(--text2)', fontSize: 13, margin: '4px 0 0' }}>
          Every transactional email Squish sends — preview, test-send, audit log. Copy edits happen in code for now.
        </p>
      </div>

      {/* Tab strip */}
      <div style={{ display: 'flex', gap: 4, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: 4, marginBottom: 20, width: 'fit-content' }}>
        {['templates', 'logs'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
            fontFamily: 'var(--font)', fontSize: 13, fontWeight: 600, textTransform: 'capitalize',
            background: tab === t ? 'var(--accent)' : 'transparent',
            color: tab === t ? 'white' : 'var(--text2)',
          }}>{t}</button>
        ))}
      </div>

      {error && (
        <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, color: '#fca5a5', fontSize: 13, marginBottom: 16 }}>
          ⚠ {error}
        </div>
      )}

      {tab === 'templates' && (
        <div className="m-stack" style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20, alignItems: 'start' }}>
          {/* Catalog */}
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: 8 }}>
            <div style={{ fontSize: 10, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.5, padding: '8px 10px 4px' }}>Catalog</div>
            {templates.length === 0 && <div style={{ padding: 14, color: 'var(--text2)', fontSize: 13 }}>No templates available.</div>}
            {templates.map(t => (
              <button
                key={t.kind}
                onClick={() => setSelected(t.kind)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '10px 12px', marginBottom: 2, borderRadius: 6,
                  border: 'none', cursor: 'pointer',
                  background: selected === t.kind ? 'rgba(124,109,250,0.15)' : 'transparent',
                  color: 'var(--text)',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600 }}>{t.label}</div>
                <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>{t.kind}</div>
              </button>
            ))}
          </div>

          {/* Preview + test-send */}
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10 }}>
            {previewLoading && <div style={{ padding: 40, color: 'var(--text2)' }}>Loading preview…</div>}
            {!previewLoading && preview && (
              <>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 10, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Subject</div>
                  <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>{preview.subject}</div>
                  {templates.find(t => t.kind === selected)?.triggers && (
                    <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 8 }}>
                      <strong style={{ color: 'var(--text)' }}>Triggered by:</strong> <code style={{ fontFamily: 'monospace' }}>{templates.find(t => t.kind === selected).triggers}</code>
                    </div>
                  )}
                  {templates.find(t => t.kind === selected)?.description && (
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 8, lineHeight: 1.5 }}>
                      {templates.find(t => t.kind === selected).description}
                    </div>
                  )}
                </div>

                {/* Rendered iframe — keeps email CSS isolated from CRM styles */}
                <div style={{ padding: 20 }}>
                  <div style={{ fontSize: 10, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Preview (HTML)</div>
                  <iframe
                    title="email preview"
                    srcDoc={preview.html}
                    sandbox="allow-same-origin"
                    style={{ width: '100%', height: 520, border: '1px solid var(--border)', borderRadius: 6, background: 'white' }}
                  />
                  <div style={{ fontSize: 10, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '14px 0 6px' }}>Sample data merged</div>
                  <pre style={{ fontSize: 11, background: 'var(--bg)', border: '1px solid var(--border)', padding: 10, borderRadius: 6, overflow: 'auto', margin: 0, color: 'var(--text2)' }}>
                    {JSON.stringify(preview.data, null, 2)}
                  </pre>
                </div>

                {/* Test send */}
                <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Test send</div>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={testTo}
                    onChange={e => setTestTo(e.target.value)}
                    style={{ flex: 1, minWidth: 200, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13 }}
                  />
                  <button data-write
                    onClick={runTestSend}
                    disabled={testing || !testTo.includes('@')}
                    style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: 'var(--accent)', color: 'white', fontSize: 13, fontWeight: 600, cursor: testing ? 'wait' : 'pointer' }}
                  >
                    {testing ? 'Sending…' : 'Send test'}
                  </button>
                  {testResult?.sent && (
                    <span style={{ fontSize: 12, color: '#4ade80' }}>✓ Sent {testResult.resendId ? `(${testResult.resendId.slice(0, 8)}…)` : ''}</span>
                  )}
                  {testResult && !testResult.sent && (
                    <span style={{ fontSize: 12, color: '#fca5a5' }}>⚠ {testResult.error || testResult.skipped || 'Failed'}</span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'logs' && (
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10 }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={logFilter.kind}
              onChange={e => setLogFilter(f => ({ ...f, kind: e.target.value }))}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13 }}
            >
              <option value="">All kinds</option>
              {templates.map(t => <option key={t.kind} value={t.kind}>{t.label}</option>)}
              {templates.map(t => <option key={`${t.kind}-test`} value={`${t.kind}-test`}>{t.label} (test)</option>)}
            </select>
            <input
              type="email"
              placeholder="Filter by recipient"
              value={logFilter.to}
              onChange={e => setLogFilter(f => ({ ...f, to: e.target.value }))}
              style={{ flex: 1, minWidth: 200, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13 }}
            />
            <button onClick={loadLogs} disabled={logsLoading} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', fontSize: 13, cursor: 'pointer' }}>
              {logsLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>

          {logsLoading && <div style={{ padding: 40, color: 'var(--text2)' }}>Loading logs…</div>}
          {!logsLoading && logs.length === 0 && <div style={{ padding: 40, color: 'var(--text2)', textAlign: 'center' }}>No sends yet (or no matches for the filter).</div>}
          {!logsLoading && logs.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>When</th>
                  <th style={thStyle}>Kind</th>
                  <th style={thStyle}>To</th>
                  <th style={thStyle}>Subject</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Resend ID</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={tdStyle}>{l.createdAt ? new Date(l.createdAt).toLocaleString() : '—'}</td>
                    <td style={tdStyle}><code style={{ fontFamily: 'monospace', fontSize: 11 }}>{l.kind}</code></td>
                    <td style={tdStyle}>{l.to}</td>
                    <td style={{ ...tdStyle, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.subject}</td>
                    <td style={tdStyle}>
                      <span style={{
                        fontSize: 10, padding: '2px 6px', borderRadius: 4,
                        background: l.status === 'sent' ? 'rgba(74,222,128,0.15)' : l.status === 'failed' ? 'rgba(239,68,68,0.15)' : 'rgba(107,114,128,0.15)',
                        color:      l.status === 'sent' ? '#4ade80' : l.status === 'failed' ? '#fca5a5' : '#9ca3af',
                      }}>{l.status}</span>
                      {l.error && <div style={{ fontSize: 10, color: '#fca5a5', marginTop: 2 }}>{l.error.slice(0, 80)}</div>}
                    </td>
                    <td style={tdStyle}><code style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--text2)' }}>{l.resendId ? l.resendId.slice(0, 14) : '—'}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

const thStyle = { textAlign: 'left', padding: '10px 14px', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text2)', fontWeight: 600 };
const tdStyle = { padding: '10px 14px', fontSize: 12, color: 'var(--text)' };
